import { AgentType, PersonType, ProofStatus, ProofTypes } from "@notabene/javascript-sdk";

export interface AddressBookWalletPayload {
	coin_network_id: string;
	wallet_address: string;
	wallet_host_type: AddressBookWalletHostType;
	wallet_host_name?: string;
	wallet_host_id?: string;
	user_owned: boolean;
	owner_legal_name: string;
	wallet_destination_tag?: string;
	wallet_host_hint: string;
	self_declared?: boolean;
	wallet_owner_type?: OwnerType;
	legal_entity_identifier?: string;
}

export interface AddressBookWalletModel {
	id: string;
	user_id: string;
	wallet_address: string;
	wallet_host_id?: string;
	wallet_host_type: AddressBookWalletHostType;
	wallet_host_name?: string;
	wallet_destination_tag?: string;
	wallet_destination_tag_type?: string;
	user_owned: boolean;
	owner_legal_name?: string;
	draft: boolean;
	asset: AddressBookWalletModelAsset;
	network: AddressBookWalletModelNetwork;
	wallet_owner_type?: OwnerType;
	legal_entity_identifier?: string;
}

export interface AddressBookWalletModelAsset {
	id: string;
	name: string;
	logo: string;
	logo_dark: string;
}

export interface AddressBookWalletModelNetwork {
	id: string;
	coin_network_id: string;
	name: string;
	logo: string;
	logo_dark: string;
	destination_tag?: {
		type: string;
	};
}

export interface WalletHostsResponse {
	data: WalletHostsResponseData;
}

export interface WalletHostsResponseData {
	all_wallet_hosts: AddressBookWalletHostModel[];
	favourite_wallet_hosts: AddressBookWalletHostModel[];
}

export interface AddressBookWalletHostModel {
	wallet_host_name: string;
	wallet_host_id?: string;
}

export enum AddressBookWalletHostExtra {
	OTHER = "other",
	SELF = "self"
}

export enum AddressBookWalletHostType {
	VASP = "VASP",
	SELF_HOSTED = "SELF_HOSTED",
	SMART_WALLET = "SMART_WALLET"
}

export enum AddressBookWalletViewType {
	NEW = "NEW",
	LEGACY = "LEGACY",
	WITHDRAW = "WITHDRAW",
	ADDRESS_BOOK = "ADDRESS_BOOK"
}

export enum AddressBookWalletAssetRipple {
	RIPPLE = "Ripple",
	XRP = "XRP"
}

export enum WalletVerificationStatus {
	SUCCESSFUL = "SUCCESSFUL",
	FAILED = "FAILED",
	IN_PROGRESS = "IN_PROGRESS",
	REQUIRED = "REQUIRED"
}

export interface WalletVerificationData {
	customer_token: string;
	notabene_asset_id: string;
	verification_status?: WalletVerificationStatus;
}

export interface AddressBookWalletTransactionData {
	transaction_id: string;
	asset_id: string;
	account_id: string;
	wallet_id: string;
	asset_amount: number;
	asset_symbol: string;
	fee_amount: number;
	transaction_type: string;
	operation_type: string;
	flow: string;
	created_at: string;
	is_index?: boolean;
	payment_data: AddressBookWalletTransactionPaymentData;
}

export interface AddressBookWalletTransactionPaymentData {
	transaction_status: string;
	broker_transaction_status: string;
	sender_legal_name: string;
	receiver_legal_name: string;
	coin_network_id: string;
	wallet_host: string;
	wallet_host_name: string;
	wallet_address: string;
	tx_hash: string;
	merchant_reference: string;
	iban: string;
	bic: string;
	card_number: string;
	address_book_entry_id?: string;
}

export enum WalletType {
	EXCHANGE = "exchange",
	SELF_HOSTED = "self_hosted"
}

export enum OnHoldWalletSelection {
	EXCHANGE = "exchange",
	SELF_HOSTED_OWN = "self_hosted_own",
	SELF_HOSTED_3RD_PARTY = "self_hosted_3rd_party"
}

// Updated OwnerType enum to match the backend expectations
export enum OwnerType {
	PERSON = "NATURAL_PERSON",
	LEGAL_ENTITY = "LEGAL_ENTITY"
}

export enum ProfileType {
	Regular = "user_type_regular",
	Business = "user_type_business"
}

export enum OnHoldDepositStep {
	SelectPersonType,
	SelectWalletType,
	ColletInfo
}

export enum OnHoldVerificationStep {
	OnHold,
	ExitWithoutVerifying,
	Loading,
	WithdrawalCancelled,
	VerificationInProgress,
	Notabene,
	ErrorNotCompleted,
	ErrorTemporaryUnavailable,
	ErrorFailed,
	ErrorPaymentTransactionNotFound,
	Success
}

export enum FlowAction {
	DEPOSIT = "deposit",
	WITHDRAWAL = "withdrawal"
}

export interface DigitalSignatureProof {
	did: string;
	status: ProofStatus;
	attestation: string;
	address: string;
	type: ProofTypes;
	proof?: string;
	confirmed?: boolean;
	wallet_provider?: string;
}

export interface NotabeneConfigInterface {
	theme: {
		primaryColor: string;
		secondaryColor: string;
		fontFamily: string;
		mode: "dark" | "light";
	};
	configuration: {
		flow: FlowAction;
		backgroundColor: string;
		accessToken: string;
		baseUrl: string;
		locale: string;
	};
	data: {
		asset: string;
		source?: string;
		destination?: string;
		amountDecimal: number;
	};
	notabeneOptions: {
		proofs: {
			reuseProof: boolean;
			fallbacks: ProofTypes[];
		};
		counterpartyAssist: {
			counterpartyTypes: PersonType[];
		};
		allowedAgentTypes: AgentType[];
		allowedCounterpartyTypes: PersonType[];
		fields: {
			naturalPerson: {
				name?: boolean;
			};
			legalPerson: Record<string, never>;
		};
	};
}
